"use client";

import { useParams, usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { LoadingWrapper } from "@/components/feedback/loading-wrapper";
import Sidebar from "@/components/layout/sidebar/sidebar";
import TopBar from "@/components/layout/top-bar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { LoadingProvider } from "@/context/loading-context";
import useBoundStore from "@/store/bound-store";
import { cn } from "@/utils/shared";
import { shouldUseMinimalLayout, shouldUseWidthConstraints } from "../_utils/route-utils";

interface AppLayoutProps {
  children: ReactNode;
}

export default function AppLayout({ children }: AppLayoutProps) {
  const pathname = usePathname() ?? "";
  const params = useParams();
  const lng = String(params.lng);
  const sidebarLayoutState = useBoundStore.use.sidebarLayoutState();

  const isProcessUrlRoute = !!params.processUrl;

  // Determine if a secondary sidebar is active (not main sidebar)
  const hasSecondarySidebar = sidebarLayoutState !== "main" && sidebarLayoutState !== null;

  // Determine layout configuration based on current route
  const useMinimalLayout = shouldUseMinimalLayout(pathname, lng, isProcessUrlRoute);
  const useWidthConstraints = shouldUseWidthConstraints(pathname, lng);

  if (useMinimalLayout) {
    const hasConstraints = useWidthConstraints || pathname.includes("/cv");
    return (
      <LoadingProvider>
        <div
          className={cn("flex min-h-[100dvh]", {
            "mx-auto": hasConstraints,
          })}
        >
          <LoadingWrapper>{children}</LoadingWrapper>
        </div>
      </LoadingProvider>
    );
  }

  return (
    <LoadingProvider>
      <SidebarProvider defaultOpen={true}>
        <div
          className={`${useWidthConstraints ? "max-w-[1600px] mx-auto" : ""} flex h-full w-full`}
        >
          {!useMinimalLayout && <Sidebar />}
          {!useMinimalLayout && <TopBar />}

          <main
            className={`${
              useMinimalLayout
                ? ""
                : hasSecondarySidebar
                  ? "md:ml-[calc(60px)] md:mt-6 mt-16 px-4 py-4 md:py-0 "
                  : "md:ml-[calc(240px)] md:mt-6 mt-16 px-4 py-4 md:py-0 "
            } w-full flex-1 flex flex-col`}
          >
            <LoadingWrapper>{children}</LoadingWrapper>
          </main>
        </div>
      </SidebarProvider>
    </LoadingProvider>
  );
}
